import { useState } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { DirectSaleReportQueryValidation, useGenerateDirectSalesRecentReport } from "@/lib/api/directSales";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/typeable_date_picker";

export function RecentReportDialog({ status }: { status?: "open" | "closed" }) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [link, setLink] = useState<string | undefined>(undefined);
  const generateRecentReport = useGenerateDirectSalesRecentReport();
  const [currentFilters, setCurrentFilters] = useState<typeof DirectSaleReportQueryValidation._type>({});

  const onGenerateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      const toParse = {
        status: status,
        gl_date: currentFilters.gl_date ? moment.tz(moment(currentFilters.gl_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).startOf('day').toDate() : undefined,
        entry_date: currentFilters.entry_date ? moment.tz(moment(currentFilters.entry_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).startOf('day').toDate() : undefined,
        range: currentFilters.range?.start_date ? {
          start_date: currentFilters.range?.start_date ? moment.tz(moment(currentFilters.range.start_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).startOf('day').toDate() : undefined,
          end_date: currentFilters.range?.end_date ? moment.tz(moment(currentFilters.range.end_date).format('YYYY-MM-DD'), "YYYY-MM-DD", CommonDestinationTimezone).endOf('day').toDate() : undefined,
        } : undefined,
      };
      const parsedFilterResult = DirectSaleReportQueryValidation.safeParse(toParse);

      if (!parsedFilterResult.success) {
        console.error("Invalid filters", parsedFilterResult.error);
        toast({
          title: "Invalid Filters",
          description: "Please check the filters and try again.",
        });
        return;
      }

      const result = await generateRecentReport(parsedFilterResult.data);
      if (!result.success) {
        throw result;
      }

      setLink(result.data.link);
    } catch (error: any) {
      console.error("Failed to generate recent report", error);
      toast({
        title: "Failed to generate recent report",
        description: error?.message || "",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          Recent Report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[98%] h-[80%] lg:max-w-[60%] md:h-[55%] flex flex-col ">
        <DialogHeader className="">
          <DialogTitle>Recent Report</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Select a date or range to filter Direct Sales
        </DialogDescription>
        <form onSubmit={onGenerateReport} className="grid mt-4 gap-2">
          <div className="flex gap-2 flex-col ">
            <Label htmlFor="entry-date" className="">
              Entry Date
            </Label>
            <DatePicker
              id="entry-date"
              value={currentFilters.entry_date ? moment(currentFilters.entry_date).toDate() : undefined}
              onChange={(date) => setCurrentFilters({ ...currentFilters, entry_date: date ? moment(date).toDate() : undefined })}
              placeholder="MM/DD/YYYY"
              className="w-full"
            />
          </div>
          <div className="flex gap-2 flex-col">
            <Label htmlFor="gl-date" className="">
              GL Date
            </Label>
            <DatePicker
              id="gl-date"
              value={currentFilters.gl_date ? moment(currentFilters.gl_date).toDate() : undefined}
              onChange={(date) => setCurrentFilters({ ...currentFilters, gl_date: date ? moment(date).toDate() : undefined })}
              placeholder="MM/DD/YYYY"
              className="col-span-3 w-full"
            />
          </div>
          <div className="flex gap-2 flex-col">
            <Label htmlFor="entry-date-range" className="">
              Entry Date Range
            </Label>
            <span className="flex gap-2">
              <DatePicker
                id="entry-date-range-start"
                value={currentFilters.range?.start_date ? moment(currentFilters.range.start_date).toDate() : undefined}
                onChange={(date) => setCurrentFilters({ ...currentFilters, range: { ...currentFilters.range, start_date: date ? moment(date).toDate() : undefined } })}
                placeholder="MM/DD/YYYY"
                className="col-span-2 w-full"
              />
              <DatePicker
                id="entry-date-range-end"
                value={currentFilters.range?.end_date ? moment(currentFilters.range.end_date).toDate() : undefined}
                onChange={(date) => setCurrentFilters({ ...currentFilters, range: { ...currentFilters.range, end_date: date ? moment(date).toDate() : undefined } })}
                placeholder="MM/DD/YYYY"
                className="col-span-2 w-full"
              />
            </span>

          </div>
          <DialogFooter className="flex flex-row justify-between items-center">
            {link && (
              <div className="flex items-center flex-col sm:flex-row gap-2 mr-4">
                <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                  Download Recent
                </a>
              </div>
            )}
            <span className="flex gap-2">
              <Button variant="outline" onClick={() => setCurrentFilters({})} disabled={isGenerating}>Close</Button>
              <Button type="submit" disabled={isGenerating}>Generate</Button>
            </span>
          </DialogFooter>

        </form>

      </DialogContent>
    </Dialog>
  );
}
